import { useEffect } from 'react';

import { faLocationDot } from '@fortawesome/pro-solid-svg-icons';
import { geoMercator, geoPath, select } from 'd3';
import * as topojson from 'topojson-client';

import {
  DeviceGroupStyles,
  FeatureShape,
  LocationWithAssetCount,
} from '../../../types';
import ukDnoTopojson from './ukDnoTopojson.json';

type MapUKProps = {
  deviceStyles?: DeviceGroupStyles;
  locations?: LocationWithAssetCount[];
  loading?: boolean;
};

// @ts-expect-error
const ukDnoRegions = topojson.feature(
  ukDnoTopojson as any,
  ukDnoTopojson.objects.dno_areas as any,
) as {
  type: 'FeatureCollection';
  features: FeatureShape[];
};

export function MapUK({ deviceStyles, locations, loading }: MapUKProps) {
  const viewboxWidth = 500;
  const viewboxHeight = 700;

  const projection = geoMercator()
    .center([-2.3252278, 55.013176])
    .translate([viewboxWidth / 2 + 40, viewboxHeight / 2])
    .scale(2400);

  useEffect(() => {
    const svg = select('div #svg-content svg')
      .attr('viewBox', `0 0 ${viewboxWidth} ${viewboxHeight}`)
      .attr('preserveAspectRatio', 'xMinYMin');

    const pathGenerator = geoPath().projection(projection);

    // There's a more elegant D3-ish way to do this, but for now remove the existing groups before regenerating the svg
    svg.selectAll('g').remove();
    svg.selectAll('text').remove();

    svg
      .append('g')
      .attr('id', 'dno-areas')
      .append('path')
      .attr(
        'class',
        `${loading ? 'fill-[#4A386E]' : 'fill-purple-90 stroke-purple-70'}`,
      )
      .datum(ukDnoRegions)
      .attr('d', pathGenerator);

    const points = (locations ?? []).map(
      ({ count, latitude, longitude, type }) => ({
        coords: projection([longitude, latitude]),
        count,
        type,
      }),
    );

    if (deviceStyles) {
      const pointsSelection = svg
        .selectAll('asset-location-pins')
        .data(points)
        .enter();

      pointsSelection
        .append('g')
        .attr('transform', (d) =>
          d.coords
            ? `translate(${d.coords[0]},${d.coords[1]}) scale(0.03)`
            : '',
        )
        .append('path')
        .attr(
          'class',
          (d) =>
            `${deviceStyles[d.type].fill} ${
              deviceStyles[d.type].stroke
            } stroke-[20px] -translate-x-[40%] -translate-y-[65%]`,
        )
        .attr('d', faLocationDot.icon[4] as any);

      pointsSelection
        .filter((d) => d.type === 'Fake')
        .append('text')
        .text((d) => `${d.count ?? 0} assets`)
        .attr('class', 'fill-purple-20 text-[12px]')
        .attr('x', (d) => d.coords?.[0] ?? 0)
        .attr('y', (d) => (d.coords?.[1] ?? 0) + 15)
        .style('text-anchor', 'middle');
    }
  }, [deviceStyles, loading, locations, projection]);

  return (
    <div
      id="svg-content"
      className=" hidden items-center justify-center lg:flex"
    >
      <svg className="max-h-screen py-8 md:py-10 md:pr-40" />
    </div>
  );
}
